import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { 
  BarChart3, TrendingUp, DollarSign, Users, Building2, Package, 
  Calendar, FileText, Receipt, Loader2, RefreshCw, ChevronRight,
  ArrowUpRight, ArrowDownRight, PiggyBank, Wallet, CreditCard,
  IndianRupee, Clock, CalendarDays
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;

// Report card configurations
const REPORT_CONFIGS = {
  report_sales: {
    key: 'report_sales',
    title: 'Sales Report',
    description: 'Daily and monthly sales overview',
    icon: TrendingUp,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
  },
  report_expenses: {
    key: 'report_expenses',
    title: 'Expenses Report',
    description: 'Expense breakdown by category',
    icon: Receipt,
    color: 'bg-red-500',
    lightColor: 'bg-red-50',
    textColor: 'text-red-600',
  },
  report_profit_loss: {
    key: 'report_profit_loss',
    title: 'Profit & Loss',
    description: 'Revenue vs expenses analysis',
    icon: BarChart3,
    color: 'bg-green-500',
    lightColor: 'bg-green-50',
    textColor: 'text-green-600',
  },
  report_vendor: {
    key: 'report_vendor',
    title: 'Vendor Report',
    description: 'Vendor payments and aging',
    icon: Building2,
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50',
    textColor: 'text-purple-600',
  },
  report_bank: {
    key: 'report_bank',
    title: 'Bank Report',
    description: 'Bank balances and transactions',
    icon: PiggyBank,
    color: 'bg-cyan-500',
    lightColor: 'bg-cyan-50',
    textColor: 'text-cyan-600',
  },
  report_salary: {
    key: 'report_salary',
    title: 'Salary Report',
    description: 'Employee payroll summary',
    icon: Users,
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    textColor: 'text-orange-600',
  },
  report_inventory: {
    key: 'report_inventory',
    title: 'Inventory Report',
    description: 'Stock levels and movements',
    icon: Package,
    color: 'bg-amber-500',
    lightColor: 'bg-amber-50',
    textColor: 'text-amber-600',
  },
  report_daily_summary: {
    key: 'report_daily_summary',
    title: 'Daily Summary',
    description: "Today's business snapshot",
    icon: Calendar,
    color: 'bg-indigo-500',
    lightColor: 'bg-indigo-50',
    textColor: 'text-indigo-600',
  },
  report_monthly_summary: {
    key: 'report_monthly_summary',
    title: 'Monthly Summary',
    description: 'Month-to-date performance',
    icon: CalendarDays,
    color: 'bg-pink-500',
    lightColor: 'bg-pink-50',
    textColor: 'text-pink-600',
  },
  report_gst: {
    key: 'report_gst',
    title: 'GST Report',
    description: 'Tax collection summary',
    icon: FileText,
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    textColor: 'text-teal-600',
  },
};

const Reports = ({ user }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [period, setPeriod] = useState('this_month');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      // Admin has all permissions
      if (user?.role === 'admin') {
        const allPerms = {};
        Object.keys(REPORT_CONFIGS).forEach(key => {
          allPerms[key] = true;
        });
        setPermissions(allPerms);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/user-access/me`, { headers });
      const effectivePerms = response.data.effective_permissions || {};
      setPermissions(effectivePerms);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableReports = () => {
    return Object.keys(REPORT_CONFIGS).filter(key => permissions[key]);
  };

  const fetchReportData = async (reportKey) => {
    setReportLoading(true);
    setSelectedReport(reportKey);
    
    try {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      
      let startDate, endDate;
      
      if (period === 'yesterday') {
        startDate = yesterday.toISOString().split('T')[0];
        endDate = yesterday.toISOString().split('T')[0];
      } else if (period === 'this_week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 6);
        startDate = weekAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (period === 'this_month') {
        startDate = thisMonthStart.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else if (period === 'last_month') {
        startDate = lastMonthStart.toISOString().split('T')[0];
        endDate = lastMonthEnd.toISOString().split('T')[0];
      }

      let data = {};

      switch (reportKey) {
        case 'report_sales':
        case 'report_daily_summary':
          const dailyRes = await axios.get(`${API}/reports/daily-summary?date=${endDate}`, { headers });
          data = { ...dailyRes.data, type: 'daily_summary' };
          break;
          
        case 'report_monthly_summary':
          const monthlyRes = await axios.get(
            `${API}/reports/monthly-summary?year=${today.getFullYear()}&month=${today.getMonth() + 1}`, 
            { headers }
          );
          data = { ...monthlyRes.data, type: 'monthly_summary' };
          break;
          
        case 'report_expenses':
          const expenseRes = await axios.get(
            `${API}/reports/expense-summary/by-category?start_date=${startDate}&end_date=${endDate}`, 
            { headers }
          );
          data = { ...expenseRes.data, type: 'expenses' };
          break;
          
        case 'report_profit_loss':
          // Get both sales and expenses for P&L
          const [salesRes, expRes] = await Promise.all([
            axios.get(`${API}/reports/monthly-summary?year=${today.getFullYear()}&month=${today.getMonth() + 1}`, { headers }),
            axios.get(`${API}/reports/expense-summary/by-category?start_date=${startDate}&end_date=${endDate}`, { headers })
          ]);
          data = {
            type: 'profit_loss',
            sales: salesRes.data,
            expenses: expRes.data,
          };
          break;
          
        case 'report_vendor':
          const vendorRes = await axios.get(`${API}/reports/vendor-aging`, { headers });
          data = { ...vendorRes.data, type: 'vendor' };
          break;
          
        case 'report_bank':
          const bankRes = await axios.get(`${API}/bank-balances`, { headers });
          data = { accounts: bankRes.data, type: 'bank' };
          break;
          
        case 'report_salary':
          const salaryMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
          const salaryRes = await axios.get(`${API}/payroll-report/${salaryMonth}`, { headers });
          data = { ...salaryRes.data, type: 'salary' };
          break;
          
        case 'report_inventory':
          // Placeholder - would need inventory endpoints
          data = { type: 'inventory', message: 'Inventory report coming soon' };
          break;
          
        case 'report_gst':
          // Calculate GST from sales data
          const gstSalesRes = await axios.get(
            `${API}/reports/monthly-summary?year=${today.getFullYear()}&month=${today.getMonth() + 1}`, 
            { headers }
          );
          const gstRate = 0.05; // 5% GST
          data = {
            type: 'gst',
            total_sales: gstSalesRes.data.total_sales,
            gst_collected: Math.round(gstSalesRes.data.total_sales * gstRate),
            gst_rate: gstRate * 100,
          };
          break;
          
        default:
          data = { type: 'unknown' };
      }

      setReportData(data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error(getErrorMessage(error, 'Failed to load report'));
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    const config = REPORT_CONFIGS[selectedReport];

    switch (reportData.type) {
      case 'daily_summary':
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Gross Sales" value={formatCurrency(reportData.total_gross_sales)} icon={TrendingUp} color="blue" />
            <StatCard label="Total Sales" value={formatCurrency(reportData.total_sales)} icon={DollarSign} color="green" />
            <StatCard label="Total Deductions" value={formatCurrency(reportData.total_deductions)} icon={ArrowDownRight} color="red" />
            <StatCard label="Net Amount" value={formatCurrency(reportData.net_amount)} icon={Wallet} color="purple" />
            <StatCard label="Day Closings" value={reportData.closings_count} icon={Calendar} color="indigo" />
            <StatCard label="Approved" value={reportData.approved_count} icon={FileText} color="teal" />
            <StatCard label="Extra Liquor" value={formatCurrency(reportData.total_extra_liquor)} icon={Receipt} color="amber" />
            <StatCard label="Discounts" value={formatCurrency(reportData.total_discounts)} icon={CreditCard} color="pink" />
          </div>
        );

      case 'monthly_summary':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Gross Sales" value={formatCurrency(reportData.total_gross_sales)} icon={TrendingUp} color="blue" />
              <StatCard label="Total Sales" value={formatCurrency(reportData.total_sales)} icon={DollarSign} color="green" />
              <StatCard label="Total Deductions" value={formatCurrency(reportData.total_deductions)} icon={ArrowDownRight} color="red" />
              <StatCard label="Net Amount" value={formatCurrency(reportData.net_amount)} icon={Wallet} color="purple" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="Days with Closings" value={reportData.closings_count} icon={Calendar} color="indigo" />
              <StatCard label="Approved Closings" value={reportData.approved_count} icon={FileText} color="teal" />
              <StatCard label="Extra Liquor Sales" value={formatCurrency(reportData.total_extra_liquor)} icon={Receipt} color="amber" />
            </div>
          </div>
        );

      case 'expenses':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <StatCard label="Total Expenses" value={formatCurrency(reportData.total)} icon={Receipt} color="red" />
              <StatCard label="Cashier Expenses" value={formatCurrency(reportData.cashier_total)} icon={Wallet} color="orange" />
              <StatCard label="Bank Expenses" value={formatCurrency(reportData.bank_total)} icon={PiggyBank} color="cyan" />
            </div>
            <div className="bg-white rounded-lg border">
              <div className="p-4 border-b">
                <h4 className="font-semibold">Category Breakdown</h4>
              </div>
              <div className="divide-y max-h-80 overflow-y-auto">
                {reportData.categories?.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                    <span className="font-medium">{cat.category || 'Uncategorized'}</span>
                    <span className="text-gray-700">{formatCurrency(cat.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'profit_loss':
        const totalRevenue = reportData.sales?.total_sales || 0;
        const totalExpenses = reportData.expenses?.total || 0;
        const profit = totalRevenue - totalExpenses;
        const margin = totalRevenue > 0 ? ((profit / totalRevenue) * 100).toFixed(1) : 0;
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Revenue" value={formatCurrency(totalRevenue)} icon={TrendingUp} color="blue" />
              <StatCard label="Total Expenses" value={formatCurrency(totalExpenses)} icon={ArrowDownRight} color="red" />
              <StatCard 
                label="Net Profit" 
                value={formatCurrency(profit)} 
                icon={profit >= 0 ? ArrowUpRight : ArrowDownRight} 
                color={profit >= 0 ? 'green' : 'red'} 
              />
              <StatCard label="Profit Margin" value={`${margin}%`} icon={BarChart3} color="purple" />
            </div>
          </div>
        );

      case 'vendor':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <StatCard label="Total Outstanding" value={formatCurrency(reportData.total_outstanding)} icon={Clock} color="red" />
              <StatCard label="Vendors with Dues" value={reportData.vendors_with_dues || 0} icon={Building2} color="purple" />
              <StatCard label="Overdue (>30 days)" value={formatCurrency(reportData.overdue_amount)} icon={ArrowDownRight} color="orange" />
            </div>
            {reportData.vendors?.length > 0 && (
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b">
                  <h4 className="font-semibold">Top Outstanding Vendors</h4>
                </div>
                <div className="divide-y max-h-80 overflow-y-auto">
                  {reportData.vendors?.slice(0, 10).map((vendor, idx) => (
                    <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                      <span className="font-medium">{vendor.vendor_name}</span>
                      <span className="text-red-600 font-semibold">{formatCurrency(vendor.pending_amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'bank':
        const totalBankBalance = reportData.accounts?.reduce((sum, acc) => sum + (acc.balance || 0), 0) || 0;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <StatCard label="Total Bank Balance" value={formatCurrency(totalBankBalance)} icon={PiggyBank} color="cyan" />
              <StatCard label="Bank Accounts" value={reportData.accounts?.length || 0} icon={Building2} color="purple" />
            </div>
            <div className="bg-white rounded-lg border">
              <div className="p-4 border-b">
                <h4 className="font-semibold">Account Balances</h4>
              </div>
              <div className="divide-y">
                {reportData.accounts?.map((acc, idx) => (
                  <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                    <div>
                      <span className="font-medium">{acc.account_name || acc.bank_account}</span>
                      <p className="text-sm text-gray-500">{acc.bank_account}</p>
                    </div>
                    <span className={`font-semibold ${acc.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(acc.balance)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'salary':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard label="Total Salary" value={formatCurrency(reportData.total_salary)} icon={Users} color="orange" />
              <StatCard label="Total Paid" value={formatCurrency(reportData.total_paid)} icon={Wallet} color="green" />
              <StatCard label="Pending" value={formatCurrency(reportData.total_pending)} icon={Clock} color="red" />
              <StatCard label="Employees" value={reportData.employee_count || 0} icon={Users} color="purple" />
            </div>
          </div>
        );

      case 'gst':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard label="Total Sales" value={formatCurrency(reportData.total_sales)} icon={TrendingUp} color="blue" />
            <StatCard label="GST Collected" value={formatCurrency(reportData.gst_collected)} icon={Receipt} color="teal" />
            <StatCard label="GST Rate" value={`${reportData.gst_rate}%`} icon={FileText} color="purple" />
          </div>
        );

      case 'inventory':
        return (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>Inventory report coming soon</p>
          </div>
        );

      default:
        return (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>Report data not available</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const availableReports = getAvailableReports();

  if (availableReports.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-600">No Reports Available</h3>
            <p className="text-gray-500 mt-2">You don&apos;t have permission to view any reports.</p>
            <p className="text-sm text-gray-400 mt-1">Contact your administrator for access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Reports</h1>
          <p className="text-gray-500 text-sm mt-1">
            {availableReports.length} report{availableReports.length !== 1 ? 's' : ''} available
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchPermissions} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Report Cards Grid */}
      {!selectedReport && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {availableReports.map(reportKey => {
            const config = REPORT_CONFIGS[reportKey];
            const Icon = config.icon;
            return (
              <Card 
                key={reportKey}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 hover:border-blue-200"
                onClick={() => fetchReportData(reportKey)}
                data-testid={`report-card-${reportKey}`}
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-lg ${config.lightColor} flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${config.textColor}`} />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{config.title}</h3>
                  <p className="text-sm text-gray-500">{config.description}</p>
                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                    View Report <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Selected Report View */}
      {selectedReport && (
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setSelectedReport(null); setReportData(null); }}
                  data-testid="back-to-reports"
                >
                  ← Back
                </Button>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {(() => {
                      const config = REPORT_CONFIGS[selectedReport];
                      const Icon = config?.icon || FileText;
                      return <Icon className={`w-5 h-5 ${config?.textColor || 'text-gray-600'}`} />;
                    })()}
                    {REPORT_CONFIGS[selectedReport]?.title || 'Report'}
                  </CardTitle>
                  <CardDescription>
                    {period === 'yesterday' ? 'Yesterday' : 
                     period === 'this_week' ? 'Last 7 Days' :
                     period === 'this_month' ? 'This Month' : 'Last Month'}
                  </CardDescription>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fetchReportData(selectedReport)}
                disabled={reportLoading}
                data-testid="refresh-report"
              >
                {reportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {reportLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : (
              renderReportContent()
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    amber: 'bg-amber-50 text-amber-600',
    cyan: 'bg-cyan-50 text-cyan-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    pink: 'bg-pink-50 text-pink-600',
    teal: 'bg-teal-50 text-teal-600',
  };

  const [bgColor, textColor] = (colorClasses[color] || colorClasses.blue).split(' ');

  return (
    <div className={`rounded-xl p-4 ${bgColor}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${textColor}`} />
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <p className={`text-xl font-bold ${textColor}`}>{value}</p>
    </div>
  );
};

export default Reports;
